import {
  AbsenceRange,
  ActualPunch,
  AttributedRequestRow,
  BreakTypeSummary,
  DailyOvertimeRange,
  DailyOvertimeTierMinutes,
  DayRun,
  ExpectedPunchesOutput,
  ExpectedRange,
  HoursBankDeposit,
  MinutesRange,
  NightWindow,
  ShiftRun,
  SplitShifts,
  TimelineRange,
  TimelineRangeKind,
  WeekWindow,
  WeeklyOvertimeRange,
  WeeklyOvertimeTagMinutes
} from './types';

export type SegmentKind = 'work' | 'break' | 'additional' | 'absence' | 'worked' | 'actual-break' | 'open-break';

export interface LaneSegment {
  start: number;
  end: number;
  kind: SegmentKind;
  label: string;
  /** Thin (break-style) vertical inset — used for absence bands drawn inside a punch-stream lane, so they read as a band inside the worked band rather than covering it. */
  thin?: boolean;
}

export interface Marker {
  minutes: number;
  label: string;
}

export interface Snap {
  key: string;
  from: number;
  to: number;
}

export interface Chip {
  label: string;
  value: string;
}

/** One numeric-minutes output field. The label is the plugin id, as for lanes, or `plugin.field` when the plugin emits more than one minutes field (unused_break_minutes.day / .night). */
export interface Metric {
  pluginId: string;
  field: string;
  label: string;
  minutes: number;
}

/** One integer-count output field (punch_counters, pending_requests_count). */
export interface Count {
  pluginId: string;
  field: string;
  value: number;
}

/** One boolean-output plugin's value. */
export interface Flag {
  pluginId: string;
  value: boolean;
}

/** Day/night minutes of one break type, keyed by the *_break_by_types plugin that emitted them. */
export interface BreakTypeRow {
  breakTypeUuid: string;
  byPlugin: Record<string, { day: number; night: number }>;
}

export interface ReportShift {
  shiftId: string;
  scheduleName: string | null;
  working: boolean | null;
  error?: string;
  expected: LaneSegment[];
  actual: LaneSegment[];
  tolerance: LaneSegment[];
  ranges: LaneSegment[];
  actualWithAbsences: LaneSegment[];
  toleranceWithAbsences: LaneSegment[];
  actualRanges: LaneSegment[];
  actualRangesWithTolerance: LaneSegment[];
  absenceRanges: LaneSegment[];
  nightRanges: LaneSegment[];
  nightRangesWithTolerance: LaneSegment[];
  earlyLateRanges: LaneSegment[];
  restDayRanges: LaneSegment[];
  nightWindow: NightWindow | null;
  splitShifts: SplitShifts | null;
  metrics: Metric[];
  flags: Flag[];
  counts: Count[];
  breakByTypes: BreakTypeRow[];
  snaps: Snap[];
  markers: Marker[];
  chips: Chip[];
  /** The requests attributed to this shift's day (attributed_requests), in engine order. */
  attributedRequests: AttributedRequestRow[];
  other: Record<string, Record<string, unknown>>;
}

/** Minutes → HH:MM; supports >24h (25:30) and negative (-01:30) shift-relative values. */
export const hhmm = (minutes: number): string => {
  const sign = minutes < 0 ? '-' : '';
  const abs = Math.abs(Math.round(minutes));
  return `${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
};

/** Minutes → `9h00`; a negative value (an hours bank below zero) keeps its sign: `-1h30`. */
export const fmtDuration = (minutes: number): string => {
  const sign = minutes < 0 ? '-' : '';
  const abs = Math.abs(Math.round(minutes));
  return `${sign}${Math.floor(abs / 60)}h${String(abs % 60).padStart(2, '0')}`;
};

const range = (start: number, end: number, kind: SegmentKind, name: string): LaneSegment => ({ start, end, kind, label: `${name} ${hhmm(start)}–${hhmm(end)}` });

const absenceSuffix = (paid?: boolean, allDay?: boolean): string => `${paid === false ? ' · unpaid' : ''}${allDay ? ' · all day' : ''}`;

/** An absence band: `thin` inset when drawn inside a punch-stream lane (over the worked band), full height in a standalone absence lane. */
const absenceSegment = (start: number, end: number, paid: boolean | undefined, allDay: boolean | undefined, thin: boolean): LaneSegment => ({
  start,
  end,
  kind: 'absence',
  label: `absence ${hhmm(start)}–${hhmm(end)}${absenceSuffix(paid, allDay)}`,
  ...(thin ? { thin: true } : {})
});

const asExpectedRanges = (v: unknown): ExpectedRange[] => (Array.isArray(v) ? v.filter((r): r is ExpectedRange => typeof r === 'object' && r !== null && typeof (r as ExpectedRange).start?.minutes === 'number' && typeof (r as ExpectedRange).end?.minutes === 'number') : []);

/**
 * Display-only pairing of the engine's ordered punch boundaries: entry→exit bounds the worked band,
 * break_startN pairs with break_endN (engine key convention); a lone break_start is an OPEN break —
 * a normal live state — drawn to the next boundary, else fallbackEnd. Orphan break_ends are dropped,
 * mirroring the engine's actual_punches plugin. Replaced by the Actual Ranges plugin when it ships.
 *
 * When the stream is absence-merged (actual_punches_with_absences / …_tolerance_and_absences), it also
 * carries absence_startN / absence_endN boundary entries; those pair by key into `absence` bands but never
 * count as punches themselves — they don't bound the worked band and don't close an open break.
 */
export const pairActualPunches = (punches: ActualPunch[], fallbackEnd: number | null): LaneSegment[] => {
  const segments: LaneSegment[] = [];
  const entry = punches.find((punch) => punch.type === 'entry');
  const exits = punches.filter((punch) => punch.type === 'exit');
  const exit = exits.length > 0 ? exits[exits.length - 1] : undefined;
  if (entry && exit) segments.push(range(entry.absoluteMinutes, exit.absoluteMinutes, 'worked', 'worked'));

  const nonAbsenceBoundaries = punches.filter((punch) => punch.type !== 'absence_start' && punch.type !== 'absence_end');

  const endsByKey = new Map(punches.filter((punch) => punch.type === 'break_end').map((punch) => [punch.key, punch]));
  punches
    .filter((punch) => punch.type === 'break_start')
    .forEach((start) => {
      const end = endsByKey.get(start.key.replace('break_start', 'break_end'));
      if (end) {
        segments.push(range(start.absoluteMinutes, end.absoluteMinutes, 'actual-break', 'break'));
        return;
      }
      const nextBoundary = nonAbsenceBoundaries.map((punch) => punch.absoluteMinutes).filter((m) => m > start.absoluteMinutes);
      const openEnd = nextBoundary.length > 0 ? Math.min(...nextBoundary) : fallbackEnd;
      if (openEnd !== null && openEnd > start.absoluteMinutes) segments.push({ start: start.absoluteMinutes, end: openEnd, kind: 'open-break', label: `open break from ${hhmm(start.absoluteMinutes)}` });
    });

  const absenceEndsByKey = new Map(punches.filter((punch) => punch.type === 'absence_end').map((punch) => [punch.key, punch]));
  punches
    .filter((punch) => punch.type === 'absence_start')
    .forEach((start) => {
      const end = absenceEndsByKey.get(start.key.replace('absence_start', 'absence_end'));
      if (end) segments.push(absenceSegment(start.absoluteMinutes, end.absoluteMinutes, start.paid, start.allDay, true));
    });
  return segments;
};

const RANGE_KINDS: Record<TimelineRangeKind, { kind: SegmentKind; name: string }> = {
  work: { kind: 'work', name: 'work' },
  break: { kind: 'break', name: 'break' },
  absence: { kind: 'absence', name: 'absence' },
  additional_work: { kind: 'additional', name: 'additional' }
};

const asTimelineRanges = (v: unknown): TimelineRange[] =>
  Array.isArray(v) ? v.filter((r): r is TimelineRange => typeof r === 'object' && r !== null && (r as TimelineRange).kind in RANGE_KINDS && typeof (r as TimelineRange).startMinutes === 'number' && typeof (r as TimelineRange).endMinutes === 'number') : [];

/** The expected_ranges timeline: the expected lane's layers already flattened by the engine into disjoint, precedence-sliced ranges. */
export const timelineRangeSegments = (ranges: TimelineRange[]): LaneSegment[] => ranges.map((r) => range(r.startMinutes, r.endMinutes, RANGE_KINDS[r.kind].kind, RANGE_KINDS[r.kind].name));

const asAbsenceRanges = (v: unknown): AbsenceRange[] => (Array.isArray(v) ? v.filter((r): r is AbsenceRange => typeof r === 'object' && r !== null && typeof (r as AbsenceRange).startMinutes === 'number' && typeof (r as AbsenceRange).endMinutes === 'number') : []);

/** attributed_requests' requests list: the day-attributed requests behind the shift's hours-bank deductions and other request-driven metrics, guarded on the fields the Report needs to place and label a row. */
const asAttributedRequests = (v: unknown): AttributedRequestRow[] =>
  Array.isArray(v)
    ? v.filter(
        (r): r is AttributedRequestRow =>
          typeof r === 'object' &&
          r !== null &&
          typeof (r as AttributedRequestRow).uuid === 'string' &&
          typeof (r as AttributedRequestRow).requestType === 'string' &&
          typeof (r as AttributedRequestRow).approvalStatus === 'string' &&
          typeof (r as AttributedRequestRow).startedAt === 'string' &&
          typeof (r as AttributedRequestRow).endedAt === 'string'
      )
    : [];

/** The it_split_shifts fields, or null when the plugin didn't run — a partial or wrongly typed output reads as "didn't run" rather than a zero count. */
const asSplitShifts = (v: unknown): SplitShifts | null => {
  if (typeof v !== 'object' || v === null) return null;
  const { count, is_breached: breached } = v as Record<string, unknown>;
  if (typeof count !== 'number' || typeof breached !== 'boolean') return null;
  return { count, isBreached: breached };
};

/**
 * A plugin's main list. The engine renamed these fields from `<plugin_id>` to plain `punches` / `ranges`
 * (plugin conventions, 2026-09-08); the old name is still read so a response from an earlier build renders.
 */
const mainField = (outputs: Record<string, Record<string, unknown>>, pluginId: string, field: 'punches' | 'ranges'): unknown => outputs[pluginId]?.[field] ?? outputs[pluginId]?.[pluginId];

const asPunches = (outputs: Record<string, Record<string, unknown>>, pluginId: string): ActualPunch[] => {
  const v = mainField(outputs, pluginId, 'punches');
  return Array.isArray(v) ? (v as ActualPunch[]) : [];
};

/**
 * An RFC3339 instant (startedAt/endedAt) as local wall clock in the request's own timezone: `YYYY-MM-DD HH:MM`.
 * Uses Intl.DateTimeFormat rather than a date library — the repo's moment dependency has no timezone
 * support, and this is the one place that needs to render an instant in a zone other than the browser's.
 * Whatever fails to parse is shown raw, which reads as "unexpected" rather than as a confident wrong answer.
 */
export const fmtRequestInstant = (instant: string, timezone: string): string => {
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return instant;
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(date);
    const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
    // A midnight hour can format as "24" in some environments' Intl implementation; normalised to "00" so the value always reads as a valid HH:MM.
    const hour = get('hour') === '24' ? '00' : get('hour');
    return `${get('year')}-${get('month')}-${get('day')} ${hour}:${get('minute')}`;
  } catch {
    return instant;
  }
};

/** The night window as `22:00–05:00 +1d`: an end past 1440 is the next morning, shown as clock time with a day marker. */
export const fmtNightWindow = (w: NightWindow): string => {
  const end = w.endMinutes >= 1440 ? `${hhmm(w.endMinutes - 1440)} +1d` : hhmm(w.endMinutes);
  return `${hhmm(w.startMinutes)}–${end}${w.applyEntirePeriod ? ' · extend' : ''}`;
};

const asNightWindow = (v: unknown): NightWindow | null => {
  if (typeof v !== 'object' || v === null) return null;
  const { startMinutes, endMinutes, applyEntirePeriod } = v as Record<string, unknown>;
  if (typeof startMinutes !== 'number' || typeof endMinutes !== 'number' || typeof applyEntirePeriod !== 'boolean') return null;
  return { startMinutes, endMinutes, applyEntirePeriod };
};

/** One minutes field of one plugin; `field` is `minutes` for every single-output metric plugin. */
export interface MetricField {
  pluginId: string;
  field: string;
}

const minutesField = (pluginId: string, field = 'minutes'): MetricField => ({ pluginId, field });

/** The label a metric is shown under: the plugin id alone when its one field is `minutes`, else `plugin.field`. */
export const metricLabel = ({ pluginId, field }: MetricField): string => (field === 'minutes' ? pluginId : `${pluginId}.${field}`);

/**
 * The minutes metric plugins (B14), grouped into the rows the report shows them in: what the schedule planned,
 * what was expected once absences are applied, what was worked, the break side, what was missed and what is paid
 * on top. Values are labelled by plugin id, as lanes are, so a number is never ambiguous about which plugin produced it.
 */
export const METRIC_ROWS: { row: string; fields: MetricField[] }[] = [
  {
    row: 'planned',
    fields: [minutesField('planned_work_minutes'), minutesField('planned_break_minutes')]
  },
  {
    row: 'expected',
    fields: [minutesField('expected_work_minutes'), minutesField('expected_break_minutes'), minutesField('expected_absences_minutes')]
  },
  {
    row: 'worked',
    fields: [minutesField('worked_minutes'), minutesField('worked_minutes_with_working_breaks'), minutesField('worked_minutes_with_tolerance'), minutesField('worked_minutes_after_absence_deduction')]
  },
  {
    row: 'breaks',
    fields: [minutesField('actual_break_minutes'), minutesField('working_break_minutes_with_tolerance'), minutesField('absences_with_overtime_minutes'), minutesField('unused_break_minutes', 'day'), minutesField('unused_break_minutes', 'night')]
  },
  {
    row: 'missed',
    fields: [minutesField('missed_minutes')]
  },
  {
    row: 'paid',
    fields: [minutesField('paid_absence_minutes'), minutesField('commute_minutes')]
  }
];

const asMetrics = (outputs: Record<string, Record<string, unknown>>): Metric[] =>
  METRIC_ROWS.flatMap((group) =>
    group.fields.flatMap((metricField) => {
      const minutes = outputs[metricField.pluginId]?.[metricField.field];
      return typeof minutes === 'number' ? [{ ...metricField, label: metricLabel(metricField), minutes }] : [];
    })
  );

/** Boolean-output plugins and the field each carries its answer in, plus the chip label shown for it. */
export const FLAG_PLUGINS: { pluginId: string; field: string; label: string }[] = [
  { pluginId: 'holiday_flag', field: 'is_holiday', label: 'holiday' },
  { pluginId: 'planned_working_day', field: 'is_working_day', label: 'planned working day' },
  { pluginId: 'missed_day', field: 'is_missed', label: 'missed day' }
];

const asFlags = (outputs: Record<string, Record<string, unknown>>): Flag[] =>
  FLAG_PLUGINS.flatMap(({ pluginId, field }) => {
    const value = outputs[pluginId]?.[field];
    return typeof value === 'boolean' ? [{ pluginId, value }] : [];
  });

/** Integer-count output fields and the chip label shown for each. A zero is shown: it says the plugin ran and counted nothing. */
export const COUNT_PLUGINS: { pluginId: string; field: string; label: string }[] = [
  { pluginId: 'punch_counters', field: 'approved', label: 'punches approved' },
  { pluginId: 'punch_counters', field: 'non_approved', label: 'punches non-approved' },
  { pluginId: 'punch_counters', field: 'invalid', label: 'punches invalid' },
  { pluginId: 'punch_counters', field: 'non_invalid_pending', label: 'punches pending (valid)' },
  { pluginId: 'pending_requests_count', field: 'count', label: 'pending requests' }
];

const asCounts = (outputs: Record<string, Record<string, unknown>>): Count[] =>
  COUNT_PLUGINS.flatMap(({ pluginId, field }) => {
    const value = outputs[pluginId]?.[field];
    return typeof value === 'number' ? [{ pluginId, field, value }] : [];
  });

const asMinutesRanges = (v: unknown): MinutesRange[] => (Array.isArray(v) ? v.filter((r): r is MinutesRange => typeof r === 'object' && r !== null && typeof (r as MinutesRange).startMinutes === 'number' && typeof (r as MinutesRange).endMinutes === 'number') : []);

/** The four early_late_ranges lists, in the order the shift meets them. Each is worked time outside the expectation, so it is drawn as `additional`. */
export const EARLY_LATE_FIELDS: { field: string; name: string }[] = [
  { field: 'early_entry', name: 'early entry' },
  { field: 'late_break_start', name: 'late break start' },
  { field: 'early_break_end', name: 'early break end' },
  { field: 'late_exit', name: 'late exit' }
];

/** One lane out of the four lists, sorted by start so the lane reads left to right whichever list a range came from. */
const asEarlyLateSegments = (output: Record<string, unknown> | undefined): LaneSegment[] =>
  output ? EARLY_LATE_FIELDS.flatMap(({ field, name }) => asMinutesRanges(output[field]).map((r) => range(r.startMinutes, r.endMinutes, 'additional', name))).sort((a, b) => a.start - b.start) : [];

/** The *_break_by_types plugins, expected side first. Each emits a `day` and a `night` list of BreakTypeSummary. */
export const BREAK_BY_TYPES_PLUGINS = ['expected_break_by_types', 'actual_break_by_types', 'actual_break_by_types_with_tolerance'] as const;

const asBreakTypeSummaries = (v: unknown): BreakTypeSummary[] =>
  Array.isArray(v) ? v.filter((r): r is BreakTypeSummary => typeof r === 'object' && r !== null && typeof (r as BreakTypeSummary).breakTypeUuid === 'string' && typeof (r as BreakTypeSummary).minutes === 'number') : [];

/** Pivots the three break-by-type outputs into one row per break type, in first-seen order, so the plugins can be read side by side. */
const asBreakByTypes = (outputs: Record<string, Record<string, unknown>>): BreakTypeRow[] => {
  const rows = new Map<string, BreakTypeRow>();
  BREAK_BY_TYPES_PLUGINS.forEach((pluginId) => {
    const output = outputs[pluginId];
    if (!output) return;
    (['day', 'night'] as const).forEach((period) =>
      asBreakTypeSummaries(output[period]).forEach((summary) => {
        const row = rows.get(summary.breakTypeUuid) ?? { breakTypeUuid: summary.breakTypeUuid, byPlugin: {} };
        const cell = row.byPlugin[pluginId] ?? { day: 0, night: 0 };
        cell[period] += summary.minutes;
        row.byPlugin[pluginId] = cell;
        rows.set(summary.breakTypeUuid, row);
      })
    );
  });
  return Array.from(rows.values());
};

/** A split-shift day is two or more blocks — a constant of the metric, so the plugin emits no field for it. */
const SPLIT_SHIFT_DAY_BLOCKS = 2;

const EXPECTED_LANES: { key: keyof ExpectedPunchesOutput; kind: SegmentKind; name: string }[] = [
  { key: 'breaks', kind: 'break', name: 'break' },
  { key: 'additional', kind: 'additional', name: 'additional' },
  { key: 'absences', kind: 'absence', name: 'absence' }
];

/** Parses one ShiftRun's outputs into the Report display model. Consumed plugins still appear in `other` so no data is hidden. */
export const buildReportShift = (shift: ShiftRun): ReportShift => {
  const outputs = shift.outputs ?? {};
  const report: ReportShift = {
    shiftId: shift.shiftId,
    scheduleName: typeof outputs.shift_source?.schedule_name === 'string' ? (outputs.shift_source.schedule_name as string) : null,
    working: typeof outputs.shift_source?.working === 'boolean' ? (outputs.shift_source.working as boolean) : null,
    error: shift.error,
    expected: [],
    actual: [],
    tolerance: [],
    ranges: [],
    actualWithAbsences: [],
    toleranceWithAbsences: [],
    actualRanges: [],
    actualRangesWithTolerance: [],
    absenceRanges: [],
    nightRanges: [],
    nightRangesWithTolerance: [],
    earlyLateRanges: [],
    restDayRanges: [],
    nightWindow: null,
    splitShifts: null,
    metrics: [],
    flags: [],
    counts: [],
    breakByTypes: [],
    snaps: [],
    markers: [],
    chips: [],
    attributedRequests: [],
    other: {}
  };
  if (shift.error) return report;

  const expected = (mainField(outputs, 'expected_punches', 'punches') ?? {}) as ExpectedPunchesOutput;
  const work = expected.work && typeof expected.work.start?.minutes === 'number' && typeof expected.work.end?.minutes === 'number' ? expected.work : null;
  if (work) report.expected.push(range(work.start.minutes, work.end.minutes, 'work', 'work'));
  EXPECTED_LANES.forEach(({ key, kind, name }) => asExpectedRanges(expected[key]).forEach((r) => report.expected.push(range(r.start.minutes, r.end.minutes, kind, name))));

  const punches = asPunches(outputs, 'actual_punches');
  report.actual = pairActualPunches(punches, work ? work.end.minutes : null);
  report.markers = punches.map((punch) => ({ minutes: punch.absoluteMinutes, label: `${punch.key} ${hhmm(punch.absoluteMinutes)}` }));

  const snapped = asPunches(outputs, 'actual_punches_with_tolerance');
  if (snapped.length > 0) {
    const actualByKey = new Map(punches.map((punch) => [punch.key, punch]));
    report.snaps = snapped.filter((punch) => actualByKey.has(punch.key) && actualByKey.get(punch.key)!.absoluteMinutes !== punch.absoluteMinutes).map((punch) => ({ key: punch.key, from: actualByKey.get(punch.key)!.absoluteMinutes, to: punch.absoluteMinutes }));
    // Always drawn when the plugin ran — a hidden lane read as "plugin didn't run"; the identical/snapped chips below say which passthrough it was.
    report.tolerance = pairActualPunches(snapped, work ? work.end.minutes : null);
  }

  // Drawn whenever the plugin ran, even when it says the same thing as the expected lane: a hidden lane reads as "plugin didn't run".
  report.ranges = timelineRangeSegments(asTimelineRanges(mainField(outputs, 'expected_ranges', 'ranges')));

  const mergedActual = asPunches(outputs, 'actual_punches_with_absences');
  report.actualWithAbsences = pairActualPunches(mergedActual, work ? work.end.minutes : null);

  const mergedTolerance = asPunches(outputs, 'actual_punches_with_tolerance_and_absences');
  report.toleranceWithAbsences = pairActualPunches(mergedTolerance, work ? work.end.minutes : null);

  report.actualRanges = timelineRangeSegments(asTimelineRanges(mainField(outputs, 'actual_ranges', 'ranges')));

  // The tolerance-adjusted actual timeline — the lane the it_split_shifts count is read off, so the blocks and the gaps between them are visible next to the number.
  report.actualRangesWithTolerance = timelineRangeSegments(asTimelineRanges(mainField(outputs, 'actual_ranges_with_tolerance', 'ranges')));

  const absenceRangeRows = asAbsenceRanges(mainField(outputs, 'absence_ranges', 'ranges'));
  report.absenceRanges = absenceRangeRows.map((r) => absenceSegment(r.startMinutes, r.endMinutes, r.paid, r.allDay, false));

  report.splitShifts = asSplitShifts(outputs.it_split_shifts);

  // Night attribution (B14.3): the pieces of the actual timelines that fall in the configured night window, raw and snapped.
  report.nightRanges = timelineRangeSegments(asTimelineRanges(outputs.night_ranges?.ranges));
  report.nightRangesWithTolerance = timelineRangeSegments(asTimelineRanges(outputs.night_ranges_with_tolerance?.ranges));
  report.nightWindow = asNightWindow(outputs.night_ranges?.night_window);

  // Where the shift was worked outside its own expectation (B14.11), and the worked stretches past the planned minutes.
  report.earlyLateRanges = asEarlyLateSegments(outputs.early_late_ranges);
  // Work on the weekly rest day (§6): the shift's worked ranges when its day is the rest day, empty on every other day.
  report.restDayRanges = timelineRangeSegments(asTimelineRanges(outputs.it_rest_day_ranges?.ranges));

  // The requests attributed to this shift's day (shift grain): what a request-driven deduction, like the hours bank's request_minutes, is actually about.
  report.attributedRequests = asAttributedRequests(outputs.attributed_requests?.requests);

  report.metrics = asMetrics(outputs);
  report.flags = asFlags(outputs);
  report.counts = asCounts(outputs);
  report.breakByTypes = asBreakByTypes(outputs);

  const breaksTotal = asExpectedRanges(expected.breaks).reduce((t, r) => t + (typeof r.duration === 'number' ? r.duration : 0), 0);
  const absences = asExpectedRanges(expected.absences).length;
  if (work) report.chips.push({ label: 'work', value: fmtDuration(work.duration) });
  if (breaksTotal > 0) report.chips.push({ label: 'breaks', value: fmtDuration(breaksTotal) });
  if (punches.length > 0) report.chips.push({ label: 'punches', value: String(punches.length) });
  if (absences > 0) report.chips.push({ label: 'absences', value: String(absences) });
  if (report.ranges.length > 0) report.chips.push({ label: 'ranges', value: String(report.ranges.length) });
  if (report.actualRanges.length > 0) report.chips.push({ label: 'actual ranges', value: String(report.actualRanges.length) });
  if (report.actualRangesWithTolerance.length > 0) report.chips.push({ label: 'actual ranges (tolerance)', value: String(report.actualRangesWithTolerance.length) });
  if (report.absenceRanges.length > 0) report.chips.push({ label: 'absence ranges', value: String(report.absenceRanges.length) });
  // A block count of 1 is still worth a chip: it says the plugin ran and found no split, which a missing chip would not.
  if (report.splitShifts) report.chips.push({ label: 'split shifts', value: `${report.splitShifts.count}${report.splitShifts.isBreached ? ' · breach' : report.splitShifts.count >= SPLIT_SHIFT_DAY_BLOCKS ? ' · split day' : ''}` });
  if (report.nightWindow) report.chips.push({ label: 'night window', value: fmtNightWindow(report.nightWindow) });
  if (report.nightRanges.length > 0) report.chips.push({ label: 'night ranges', value: String(report.nightRanges.length) });
  if (report.earlyLateRanges.length > 0) report.chips.push({ label: 'early/late ranges', value: String(report.earlyLateRanges.length) });
  if (report.restDayRanges.length > 0) report.chips.push({ label: 'rest day ranges', value: String(report.restDayRanges.length) });
  if (report.attributedRequests.length > 0) report.chips.push({ label: 'requests', value: String(report.attributedRequests.length) });
  report.flags.forEach((flag) => report.chips.push({ label: FLAG_PLUGINS.find((f) => f.pluginId === flag.pluginId)!.label, value: flag.value ? 'yes' : 'no' }));
  report.counts.forEach((count) => report.chips.push({ label: COUNT_PLUGINS.find((c) => c.pluginId === count.pluginId && c.field === count.field)!.label, value: String(count.value) }));
  if (report.snaps.length > 0) report.chips.push({ label: 'snapped', value: String(report.snaps.length) });
  else if (snapped.length > 0) report.chips.push({ label: 'tolerance', value: 'identical' });

  report.other = Object.fromEntries(Object.entries(outputs).filter(([, fields]) => fields && typeof fields === 'object')) as Record<string, Record<string, unknown>>;
  return report;
};

/** One daily-overtime stretch as shown: the interval, its tier and the tier's percent. */
export interface OvertimeStretch {
  start: number;
  end: number;
  tier: string;
  percent: number;
  label: string;
}

/** One weekly-overtime stretch as shown: the interval, its tag/tier — priced on the table row, so not repeated here — and the label. */
export interface WeeklyOvertimeStretch {
  start: number;
  end: number;
  tag: string;
  tier: string;
  label: string;
}

/** The day-grain half of one date: what the plugins that run once per date over its shifts produced. */
/** The chips of one day-grain plugin, shown on one line under the plugin id so a reader sees which plugin each number came from. */
export interface ChipGroup {
  pluginId: string;
  chips: Chip[];
}

export interface ReportDay {
  date: string;
  error?: string;
  /** Every chip of every group, in group order. Kept flat for callers that only need the values. */
  chips: Chip[];
  /** The chips grouped by the plugin that produced them, in the order the plugins are read below. */
  groups: ChipGroup[];
  /** The rest-day worked stretches of the date (it_rest_day_worked.ranges), in clock order, as `09:00–13:00`. */
  restDayStretches: string[];
  overtimeTiers: DailyOvertimeTierMinutes[];
  overtimeStretches: OvertimeStretch[];
  weeklyOvertimeRows: WeeklyOvertimeTagMinutes[];
  weeklyOvertimeStretches: WeeklyOvertimeStretch[];
  hoursBankDeposits: HoursBankDeposit[];
  other: Record<string, Record<string, unknown>>;
}

const asWeekWindow = (v: unknown): WeekWindow | null => {
  if (typeof v !== 'object' || v === null) return null;
  const { open, close } = v as Record<string, unknown>;
  if (typeof open !== 'string' || typeof close !== 'string') return null;
  return { open, close };
};

/**
 * A wall-clock instant as `YYYY-MM-DD HH:MM`: the seconds are always zero on a week boundary, the date is not, since the window spans days.
 * Anchored at both ends on purpose. it_week_anchor's contract is a naive local instant carrying no zone offset, so anything with a tail —
 * `…:00Z`, `…:00+02:00` — is not the value this chip claims to render: dropping the tail would show a UTC instant as if it were the local
 * anchor. Whatever does not match the layout exactly is shown raw, which reads as "unexpected" rather than as a confident wrong answer.
 */
const fmtWallClock = (instant: string): string => {
  const parts = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}):\d{2}$/.exec(instant);
  return parts ? `${parts[1]} ${parts[2]}` : instant;
};

/** The week window as `2026-08-03 00:00 → 2026-08-10 00:00`. The anchor is configurable, so the weekday is not implied and both instants are shown. */
export const fmtWeekWindow = (window: WeekWindow): string => `${fmtWallClock(window.open)} → ${fmtWallClock(window.close)}`;

const asOvertimeTiers = (v: unknown): DailyOvertimeTierMinutes[] =>
  Array.isArray(v)
    ? v.filter((t): t is DailyOvertimeTierMinutes => typeof t === 'object' && t !== null && typeof (t as DailyOvertimeTierMinutes).tier === 'string' && typeof (t as DailyOvertimeTierMinutes).percent === 'number' && typeof (t as DailyOvertimeTierMinutes).minutes === 'number')
    : [];

const asOvertimeStretches = (v: unknown): OvertimeStretch[] =>
  Array.isArray(v)
    ? v
        .filter(
          (r): r is DailyOvertimeRange =>
            typeof r === 'object' && r !== null && typeof (r as DailyOvertimeRange).startMinutes === 'number' && typeof (r as DailyOvertimeRange).endMinutes === 'number' && typeof (r as DailyOvertimeRange).tier === 'string' && typeof (r as DailyOvertimeRange).percent === 'number'
        )
        .map((r) => ({ start: r.startMinutes, end: r.endMinutes, tier: r.tier, percent: r.percent, label: `${hhmm(r.startMinutes)}–${hhmm(r.endMinutes)} · ${r.tier} ${r.percent}%` }))
    : [];

const asWeeklyOvertimeRows = (v: unknown): WeeklyOvertimeTagMinutes[] =>
  Array.isArray(v)
    ? v.filter(
        (t): t is WeeklyOvertimeTagMinutes =>
          typeof t === 'object' &&
          t !== null &&
          typeof (t as WeeklyOvertimeTagMinutes).tag === 'string' &&
          typeof (t as WeeklyOvertimeTagMinutes).tier === 'string' &&
          typeof (t as WeeklyOvertimeTagMinutes).percent === 'number' &&
          typeof (t as WeeklyOvertimeTagMinutes).minutes === 'number'
      )
    : [];

const asWeeklyOvertimeStretches = (v: unknown): WeeklyOvertimeStretch[] =>
  Array.isArray(v)
    ? v
        .filter(
          (r): r is WeeklyOvertimeRange =>
            typeof r === 'object' && r !== null && typeof (r as WeeklyOvertimeRange).startMinutes === 'number' && typeof (r as WeeklyOvertimeRange).endMinutes === 'number' && typeof (r as WeeklyOvertimeRange).tag === 'string' && typeof (r as WeeklyOvertimeRange).tier === 'string'
        )
        .map((r) => ({ start: r.startMinutes, end: r.endMinutes, tag: r.tag, tier: r.tier, label: `${hhmm(r.startMinutes)}–${hhmm(r.endMinutes)}` }))
    : [];

/** it_hours_bank.balance_minutes_by_deposit rows (§11), kept in engine order (oldest first) so the table reads as the FIFO queue the bank drains. */
const asHoursBankDeposits = (v: unknown): HoursBankDeposit[] =>
  Array.isArray(v) ? v.filter((d): d is HoursBankDeposit => typeof d === 'object' && d !== null && typeof (d as HoursBankDeposit).earnedOn === 'string' && typeof (d as HoursBankDeposit).expiresOn === 'string' && typeof (d as HoursBankDeposit).minutes === 'number') : [];

/** The hours-bank counters that only matter when something happened today: a zero would just repeat the balance chip, so each one is a chip only when non-zero. */
const HOURS_BANK_MOVEMENTS: { field: string; label: string }[] = [
  { field: 'deposited_minutes', label: 'bank deposited' },
  { field: 'missed_minutes_covered', label: 'bank covered missed' },
  { field: 'missed_minutes_remaining', label: 'missed remaining' },
  { field: 'request_minutes', label: 'bank covered requests' },
  { field: 'expired_negative_minutes', label: 'bank debt expired' }
];

/** A date string chip value: the engine sends "" for "no date", which is not a date to print. */
const asDate = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

const yesNo = (v: boolean): string => (v ? 'yes' : 'no');

/**
 * The fallback for a day-grain plugin this page has no hand-made chips for: every scalar field becomes a chip named after the field,
 * so a plugin that lands after this build is still readable on the row and not only in the raw JSON. Minutes fields are formatted as
 * durations, booleans as yes/no, an empty string is skipped (the engine's "no value"). Lists and objects stay in the day outputs JSON.
 */
export const genericChips = (output: Record<string, unknown>): Chip[] =>
  Object.entries(output).flatMap(([field, value]) => {
    const label = field.replace(/_/g, ' ');
    if (typeof value === 'number') return [{ label, value: field === 'minutes' || field.endsWith('_minutes') ? fmtDuration(value) : String(value) }];
    if (typeof value === 'boolean') return [{ label, value: yesNo(value) }];
    if (typeof value === 'string' && value !== '') return [{ label, value }];
    return [];
  });

/**
 * The expiry chip: the two expired counters (paid out vs forfeited) are one event for the reader — "the bank lost this much today" — so they share one chip.
 * The label names the fate when only one applies; when both do, the value spells out the split so neither number is hidden inside the sum.
 */
const hoursBankExpiredChip = (output: Record<string, unknown>): Chip | null => {
  const paid = typeof output.expired_paid_minutes === 'number' ? output.expired_paid_minutes : 0;
  const forfeited = typeof output.expired_forfeited_minutes === 'number' ? output.expired_forfeited_minutes : 0;
  if (paid === 0 && forfeited === 0) return null;
  if (forfeited === 0) return { label: 'bank expired paid', value: fmtDuration(paid) };
  if (paid === 0) return { label: 'bank expired forfeited', value: fmtDuration(forfeited) };
  return { label: 'bank expired', value: `${fmtDuration(paid + forfeited)} · ${fmtDuration(paid)} paid · ${fmtDuration(forfeited)} forfeited` };
};

/** `contractual` and `weekly` rows are priced tags — their `percent` is the uplift the minutes earn. The other rows are counts (ordinary time, or minutes already paid by another plugin), so a printed `0%` would misread them as a priced tag at zero uplift. */
export const fmtWeeklyOvertimePercent = (row: WeeklyOvertimeTagMinutes): string => (row.tag === 'contractual' || row.tag === 'weekly' ? `${row.percent}%` : '—');

/** Parses one DayRun's outputs into the Report display model. Consumed plugins still appear in `other` so no data is hidden. */
export const buildReportDay = (day: DayRun): ReportDay => {
  const outputs = day.outputs ?? {};
  const report: ReportDay = { date: day.date, error: day.error, chips: [], groups: [], restDayStretches: [], overtimeTiers: [], overtimeStretches: [], weeklyOvertimeRows: [], weeklyOvertimeStretches: [], hoursBankDeposits: [], other: {} };
  if (day.error) return report;

  // One group per plugin, pushed only when it produced a chip, so a plugin that did not run leaves no empty line.
  const consumed = new Set<string>();
  const group = (pluginId: string, chips: Chip[]) => {
    consumed.add(pluginId);
    if (chips.length > 0) report.groups.push({ pluginId, chips });
  };

  const streak = outputs.working_days_in_row?.count;
  group('working_days_in_row', typeof streak === 'number' ? [{ label: 'working days in row', value: String(streak) }] : []);

  // The week every weekly Italian metric is measured against (§0.1). It is the same value all week, so it reads as context for the chips after it rather than a per-day number.
  const weekWindow = asWeekWindow(outputs.it_week_anchor?.week_window);
  group('it_week_anchor', weekWindow ? [{ label: 'week', value: fmtWeekWindow(weekWindow) }] : []);

  // §2 multiperiodalità: the threshold is in hours, not minutes — it is a contractual week length, not a measured duration, so fmtDuration would be the wrong unit.
  const multiperiodalitaChips: Chip[] = [];
  const weeklyThreshold = outputs.it_multiperiodalita?.weekly_overtime_threshold;
  if (typeof weeklyThreshold === 'number') multiperiodalitaChips.push({ label: 'weekly threshold', value: `${weeklyThreshold}h` });
  // A warning verdict, never a block: the plan is allowed to run over its average ceiling, and the tool only reports which way it fell.
  const withinCeiling = outputs.it_multiperiodalita?.plan_within_ceiling;
  if (typeof withinCeiling === 'boolean') multiperiodalitaChips.push({ label: 'plan within ceiling', value: yesNo(withinCeiling) });
  group('it_multiperiodalita', multiperiodalitaChips);

  report.overtimeTiers = asOvertimeTiers(outputs.it_daily_overtime?.minutes_by_tier);
  report.overtimeStretches = asOvertimeStretches(outputs.it_daily_overtime?.ranges);
  // The total is shown as a chip; the per-tier split and the stretches are the table below it. A zero total still says the plugin ran.
  group('it_daily_overtime', outputs.it_daily_overtime ? [{ label: 'daily overtime', value: fmtDuration(report.overtimeTiers.reduce((total, tier) => total + tier.minutes, 0)) }] : []);

  report.weeklyOvertimeRows = asWeeklyOvertimeRows(outputs.it_weekly_overtime?.minutes_by_tag);
  report.weeklyOvertimeStretches = asWeeklyOvertimeStretches(outputs.it_weekly_overtime?.ranges);
  const weekly: Chip[] = [];
  if (outputs.it_weekly_overtime) {
    // The weekly total, like the daily one: a chip, not just the table below it. A zero still says the plugin ran and found nothing past the threshold.
    weekly.push({ label: 'weekly overtime', value: fmtDuration(report.weeklyOvertimeRows.filter((row) => row.tag === 'weekly').reduce((total, row) => total + row.minutes, 0)) });
    // The week's running total is the number the threshold is compared against, so a reader can see how far into the week this day sits without opening the raw JSON.
    if (typeof outputs.it_weekly_overtime.week_minutes === 'number') weekly.push({ label: 'week total', value: fmtDuration(outputs.it_weekly_overtime.week_minutes) });
  }
  group('it_weekly_overtime', weekly);

  // §9 overtime treatment: where today's overtime went, as the three report totals. The per-tier split is it_daily_overtime's and it_weekly_overtime's tables above,
  // and the policy's choice itself is a param, not an output, so there is no chip for it. The premium is a rated figure (minutes × percent), the other two are raw.
  const treatment = outputs.it_overtime_treatment;
  const treatmentChips: Chip[] = [];
  if (treatment) {
    if (typeof treatment.paid_minutes === 'number') treatmentChips.push({ label: 'paid overtime', value: fmtDuration(treatment.paid_minutes) });
    if (typeof treatment.premium_paid_minutes === 'number' && treatment.premium_paid_minutes !== 0) treatmentChips.push({ label: 'banked premium paid', value: fmtDuration(treatment.premium_paid_minutes) });
    if (typeof treatment.banked_minutes === 'number') {
      // The expiry date travels with the deposit it dates: a banked amount with no date is a zero deposit, and the engine sends "" for it.
      const expiresOn = asDate(treatment.banked_expires_on);
      treatmentChips.push({ label: 'banked today', value: `${fmtDuration(treatment.banked_minutes)}${expiresOn ? ` · expires ${expiresOn}` : ''}` });
    }
  }
  group('it_overtime_treatment', treatmentChips);

  // §11 hours bank: the balance is always chipped when the plugin ran — a zero balance says "the bank is empty", not "no bank". The movements are chipped only when they moved.
  report.hoursBankDeposits = asHoursBankDeposits(outputs.it_hours_bank?.balance_minutes_by_deposit);
  const bank = outputs.it_hours_bank;
  const bankChips: Chip[] = [];
  if (bank) {
    if (typeof bank.balance_minutes === 'number') bankChips.push({ label: 'hours bank balance', value: fmtDuration(bank.balance_minutes) });
    for (const { field, label } of HOURS_BANK_MOVEMENTS) {
      const minutes = bank[field];
      if (typeof minutes === 'number' && minutes !== 0) bankChips.push({ label, value: fmtDuration(minutes) });
    }
    const expired = hoursBankExpiredChip(bank);
    if (expired) bankChips.push(expired);
  }
  group('it_hours_bank', bankChips);

  // §5 annual overtime cap: three period-to-date figures, always shown when the plugin ran, and the two verdicts only when they say something —
  // period_closes and exceeded are false on every ordinary date, and a row of "no" every day would hide the one day they matter.
  const cap = outputs.it_annual_overtime_cap;
  const capChips: Chip[] = [];
  if (cap) {
    const periodOpen = asDate(cap.period_open);
    if (periodOpen) capChips.push({ label: 'annual period from', value: periodOpen });
    if (typeof cap.performed_minutes === 'number') capChips.push({ label: 'overtime performed', value: fmtDuration(cap.performed_minutes) });
    if (typeof cap.taken_back_minutes === 'number') capChips.push({ label: 'overtime taken back', value: fmtDuration(cap.taken_back_minutes) });
    if (typeof cap.balance_minutes === 'number') capChips.push({ label: 'annual balance', value: fmtDuration(cap.balance_minutes) });
    if (cap.period_closes === true) capChips.push({ label: 'annual period closes', value: 'yes' });
    if (typeof cap.exceeded === 'boolean' && (cap.exceeded || cap.period_closes === true)) capChips.push({ label: 'cap exceeded', value: yesNo(cap.exceeded) });
  }
  group('it_annual_overtime_cap', capChips);

  // §7.1 night worker: the night minutes and the year's qualifying-day count are per-date figures; the status is one chip with the date it took effect.
  const nightWorker = outputs.it_night_worker;
  const nightWorkerChips: Chip[] = [];
  if (nightWorker) {
    if (typeof nightWorker.minutes === 'number') nightWorkerChips.push({ label: 'night minutes', value: fmtDuration(nightWorker.minutes) });
    if (typeof nightWorker.qualifying_days === 'number') nightWorkerChips.push({ label: 'qualifying nights this year', value: String(nightWorker.qualifying_days) });
    if (typeof nightWorker.is_classified === 'boolean') {
      const since = asDate(nightWorker.classified_since);
      nightWorkerChips.push({ label: 'night worker', value: `${yesNo(nightWorker.is_classified)}${nightWorker.is_classified && since ? ` · since ${since}` : ''}` });
    }
  }
  group('it_night_worker', nightWorkerChips);

  // §7.2 night worker limit: the week-to-date average over the days that fed it, the ceiling verdict, and the two counters only when they count (1).
  const nightLimit = outputs.it_night_worker_limit;
  const nightLimitChips: Chip[] = [];
  if (nightLimit) {
    if (typeof nightLimit.average_minutes === 'number') {
      const days = typeof nightLimit.week_days === 'number' ? ` · ${nightLimit.week_days} day${nightLimit.week_days === 1 ? '' : 's'}` : '';
      nightLimitChips.push({ label: 'night limit week average', value: `${fmtDuration(nightLimit.average_minutes)}${days}` });
    }
    if (typeof nightLimit.is_exceeded === 'boolean') nightLimitChips.push({ label: 'over night ceiling', value: yesNo(nightLimit.is_exceeded) });
    if (nightLimit.exceeded === 1) nightLimitChips.push({ label: 'night limit breach counted', value: 'yes' });
    if (nightLimit.nights === 1) nightLimitChips.push({ label: 'night worked', value: 'yes' });
  }
  group('it_night_worker_limit', nightLimitChips);

  // §6 rest day worked: the minutes and, when the treatment rates them differently, the rated figure; the stretches are listed after the chips.
  // owed_days is a running total, so it is shown whenever the plugin ran — a zero says nothing is owed, not that the plugin was absent.
  const restDay = outputs.it_rest_day_worked;
  const restDayChips: Chip[] = [];
  if (restDay) {
    report.restDayStretches = asTimelineRanges(restDay.ranges).map((r) => `${hhmm(r.startMinutes)}–${hhmm(r.endMinutes)}`);
    if (typeof restDay.minutes === 'number') restDayChips.push({ label: 'rest day worked', value: fmtDuration(restDay.minutes) });
    if (typeof restDay.rated_minutes === 'number' && restDay.rated_minutes !== restDay.minutes) restDayChips.push({ label: 'rest day rated', value: fmtDuration(restDay.rated_minutes) });
    if (typeof restDay.owed_days === 'number') restDayChips.push({ label: 'compensatory rest days owed', value: String(restDay.owed_days) });
  }
  group('it_rest_day_worked', restDayChips);

  // §3 weekly hours average: the period-to-date average over the weeks behind it; the verdict only on the closing date, where it is decided.
  const weeklyAverage = outputs.it_weekly_hours_average;
  const weeklyAverageChips: Chip[] = [];
  if (weeklyAverage) {
    const periodOpen = asDate(weeklyAverage.period_open);
    if (periodOpen) weeklyAverageChips.push({ label: 'reference period from', value: fmtWallClock(periodOpen) });
    if (typeof weeklyAverage.average_minutes === 'number') {
      const weeks = typeof weeklyAverage.weeks_counted === 'number' ? ` · ${weeklyAverage.weeks_counted} week${weeklyAverage.weeks_counted === 1 ? '' : 's'}` : '';
      weeklyAverageChips.push({ label: 'weekly average', value: `${fmtDuration(weeklyAverage.average_minutes)}${weeks}` });
    }
    if (weeklyAverage.period_closes === true) weeklyAverageChips.push({ label: 'reference period closes', value: 'yes' });
    if (typeof weeklyAverage.exceeded === 'boolean' && (weeklyAverage.exceeded || weeklyAverage.period_closes === true)) weeklyAverageChips.push({ label: 'weekly ceiling exceeded', value: yesNo(weeklyAverage.exceeded) });
  }
  group('it_weekly_hours_average', weeklyAverageChips);

  // Every other day-grain plugin: its scalars as chips, so a new plugin shows up on the row the day it lands.
  Object.keys(outputs)
    .filter((pluginId) => !consumed.has(pluginId) && outputs[pluginId] && typeof outputs[pluginId] === 'object')
    .sort()
    .forEach((pluginId) => group(pluginId, genericChips(outputs[pluginId])));

  report.chips = report.groups.flatMap((g) => g.chips);
  report.other = Object.fromEntries(Object.entries(outputs).filter(([, fields]) => fields && typeof fields === 'object')) as Record<string, Record<string, unknown>>;
  return report;
};
